import React from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import useSwipeableByRef from "../../hooks/useSwipeableByRef";

import "./carousel.scss";
import CarouselDots from "./CarouselDots";

function Carousel({
  children,
  className,
  defaultSelected,
}: {
  children: React.ReactNode;
  className?: string;
  defaultSelected?: number;
}) {
  const slides = React.useMemo(() => {
    let res = [];

    if (Array.isArray(children)) {
      for (const c of children) {
        if (Array.isArray(c)) {
          res.push(...c);
        } else {
          res.push(c);
        }
      }
    } else {
      res = [children];
    }

    return res;
  }, [children]);

  const [selected, changeSelected] = React.useState<number>(
    defaultSelected ?? slides.length - 1
  );

  const [slideType, changeSlideType] = React.useState("next");
  const prevSlide = React.useCallback(() => {
    changeSlideType("prev");
    changeSelected((s) => (s - 1 >= 0 ? s - 1 : slides.length - 1));
  }, [slides.length, changeSelected]);

  const nextSlide = React.useCallback(() => {
    changeSlideType("next");
    changeSelected((s) => (s + 1 <= slides.length - 1 ? s + 1 : 0));
  }, [slides.length, changeSelected]);

  const toSlide = React.useCallback(
    (i: number) => {
      changeSelected((old) => {
        changeSlideType(i > old ? "next" : "prev");
        return i;
      });
    },
    [changeSelected]
  );

  const sliderRef = React.useRef() as React.RefObject<HTMLDivElement>;
  React.useLayoutEffect(() => {
    const node = sliderRef.current;

    const keyDownListener = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowLeft":
          prevSlide();
          break;
        case "ArrowRight":
          nextSlide();
          break;
      }
    };

    if (node) {
      node.addEventListener("keydown", keyDownListener);
      return () => {
        node.removeEventListener("keydown", keyDownListener);
      };
    }
  }, [nextSlide, prevSlide]);

  // useAutoFocus(sliderRef, [selected]);

  useSwipeableByRef(sliderRef, {
    onSwipedLeft: () => nextSlide(),
    onSwipedRight: () => prevSlide(),
    delta: 4,
  });

  return (
    <>
      <CarouselDots slides={slides} selected={selected} onChange={toSlide} />
      <div
        ref={sliderRef}
        className={[className, "carousel"].filter((e) => e).join(" ")}
        tabIndex={0}
      >
        <TransitionGroup
          component={null}
          childFactory={(child) =>
            React.cloneElement(child, {
              classNames:
                slideType === "prev" ? "left-to-right" : "right-to-left",
            })
          }
        >
          <CSSTransition timeout={500} key={selected}>
            {slides[selected]}
          </CSSTransition>
        </TransitionGroup>
      </div>
    </>
  );
}

export { Carousel as default };
